// Wildlife data for El Porvenir, New Mexico
// Each animal has rich profile info, fun facts, and a curated image.

export const ANIMALS = [
  {
    id: "rio-grande-cutthroat-trout",
    number: "01",
    name: "Rio Grande Cutthroat Trout",
    scientific: "Oncorhynchus clarkii virginalis",
    category: "Fish",
    habitat: "Cold, clear headwater streams of the Sangre de Cristo range",
    diet: "Aquatic insects, terrestrial bugs, smaller fish",
    size: "8–14 in (20–36 cm)",
    status: "New Mexico State Fish · Sensitive Species",
    image: "https://images.pexels.com/photos/28575465/pexels-photo-28575465.jpeg",
    blurb:
      "The native trout of northern New Mexico, named for the crimson slash beneath its jaw. Once on the edge of disappearing, careful stream restoration has brought it back to the cold creeks near El Porvenir.",
    facts: [
      "Named for the deep red ‘cutthroat’ slash mark beneath the jaw.",
      "The only trout truly native to the Rio Grande basin.",
      "Designated the official state fish of New Mexico in 1955.",
      "Spawns in late spring when water temps creep above 41°F.",
    ],
    layout: "wide",
  },
  {
    id: "mule-deer",
    number: "02",
    name: "Mule Deer",
    scientific: "Odocoileus hemionus",
    category: "Mammal",
    habitat: "Piñon-juniper woodland, ponderosa forest, mountain meadows",
    diet: "Browser — shrubs, acorns, forbs, wildflowers",
    size: "3–3.5 ft at shoulder · 130–280 lb",
    status: "Common · Big-game species",
    image: "https://images.unsplash.com/photo-1484406566174-9da000fda645",
    blurb:
      "Named for their oversized ears, mule deer are the unhurried wanderers of El Porvenir's slopes. You may spot them at dusk on the canyon road, gracefully bouncing on all fours in a gait called ‘stotting.’",
    facts: [
      "Their bounding ‘stot’ gait can clear obstacles 15 ft wide.",
      "Antlers fork twice, unlike the single-beam whitetail rack.",
      "Ears rotate independently like radar dishes to triangulate sound.",
      "Bucks shed their antlers each winter and regrow them in spring.",
    ],
    layout: "tall",
  },
  {
    id: "coyote",
    number: "03",
    name: "Coyote",
    scientific: "Canis latrans",
    category: "Mammal · Canid",
    habitat: "Everywhere — meadow, forest, town outskirts",
    diet: "Omnivore — rodents, rabbits, fruit, carrion",
    size: "20–50 lb · 32–37 in long",
    status: "Abundant · Trickster of folklore",
    image: "https://images.pexels.com/photos/35677728/pexels-photo-35677728.jpeg",
    blurb:
      "The voice of the New Mexico night. Coyotes are clever generalists who have actually expanded their range as other predators retreated. Listen for their yip-and-howl chorus echoing through the village after dark.",
    facts: [
      "Their scientific name latrans means ‘barking’ in Latin.",
      "Can run up to 40 mph in short bursts.",
      "A pair’s chorus can sound like a dozen animals — vocal trickery.",
      "Central to Diné and Pueblo storytelling as both trickster and teacher.",
    ],
    layout: "square",
  },
  {
    id: "black-tailed-jackrabbit",
    number: "04",
    name: "Black-Tailed Jackrabbit",
    scientific: "Lepus californicus",
    category: "Mammal · Hare",
    habitat: "Open grassland, sage flats, low scrub",
    diet: "Grasses, mesquite, cactus, sage",
    size: "4–8 lb · 18–25 in",
    status: "Common",
    image: "https://images.pexels.com/photos/31677506/pexels-photo-31677506.jpeg",
    blurb:
      "Not really a rabbit at all — a hare. Long-limbed and built for speed, the black-tail bursts from cover in great zig-zag leaps, ears flashing pink in the high desert sun.",
    facts: [
      "Can leap up to 20 feet in a single bound.",
      "Sprints at 40 mph to outrun coyotes and hawks.",
      "Ears act as radiators — releasing heat through dense blood vessels.",
      "Babies (leverets) are born fully furred with eyes open.",
    ],
    layout: "square",
  },
  {
    id: "black-bear",
    number: "05",
    name: "Black Bear",
    scientific: "Ursus americanus",
    category: "Mammal · Bear",
    habitat: "Mixed conifer forest, oak thickets, canyon riparian",
    diet: "Omnivore — berries, acorns, insects, carrion, the occasional trash can",
    size: "150–500 lb",
    status: "Common in Sangre de Cristos · New Mexico State Mammal",
    image: "https://images.pexels.com/photos/36075363/pexels-photo-36075363.jpeg",
    blurb:
      "The largest mammal you're likely to encounter near El Porvenir. Despite the name, black bears around here come in cinnamon, blonde, and chocolate. Store food well — they remember every cabin.",
    facts: [
      "New Mexico's official state mammal since 1963.",
      "Can smell food from over a mile away.",
      "‘Smokey Bear’ was a real cub rescued from a 1950 fire in Lincoln NF.",
      "Climbs trees easily, even as a 400 lb adult.",
    ],
    layout: "wide",
  },
  {
    id: "plateau-fence-lizard",
    number: "06",
    name: "Plateau Fence Lizard",
    scientific: "Sceloporus tristichus",
    category: "Reptile",
    habitat: "Sun-warmed rocks, log piles, adobe walls",
    diet: "Insectivore — ants, beetles, spiders, flies",
    size: "3.5–6 in (with tail)",
    status: "Common",
    image: "https://images.pexels.com/photos/12744960/pexels-photo-12744960.jpeg",
    blurb:
      "The small spiny lizard you'll catch doing push-ups on a sun-warmed rock. The blue belly patches flash a warning to rivals — a tiny enamel of sky on a scaly back.",
    facts: [
      "Males flash bright blue belly patches to defend territory.",
      "Their ‘push-up’ display says ‘this rock is mine.’",
      "Body temperature is controlled by sun-basking, not metabolism.",
      "Studies suggest their blood may help reduce Lyme bacteria in ticks.",
    ],
    layout: "square",
  },
  {
    id: "pronghorn",
    number: "07",
    name: "Pronghorn",
    scientific: "Antilocapra americana",
    category: "Mammal",
    habitat: "High plains east of the Sangre de Cristos",
    diet: "Sagebrush, forbs, cactus",
    size: "75–140 lb · 32–41 in at shoulder",
    status: "Common on grasslands",
    image: "https://images.pexels.com/photos/13212749/pexels-photo-13212749.jpeg",
    blurb:
      "Often called the ‘American antelope,’ but the pronghorn is its own thing — the last survivor of an ancient family. The fastest land animal in the Western Hemisphere, built to outrun a predator that's been extinct for 10,000 years.",
    facts: [
      "Second-fastest land animal on Earth (~55 mph) — only cheetahs are quicker.",
      "Evolved their speed to outrun the now-extinct American cheetah.",
      "Eyesight equivalent to 8x binoculars — they can spot motion 4 miles away.",
      "Sheds and regrows the outer sheath of its horns each year.",
    ],
    layout: "tall",
  },
  {
    id: "broad-tailed-hummingbird",
    number: "08",
    name: "Broad-Tailed Hummingbird",
    scientific: "Selasphorus platycercus",
    category: "Bird",
    habitat: "Montane meadows, aspen edges, sub-alpine flowers",
    diet: "Nectar, tiny insects, tree sap",
    size: "3.5–4 in · 0.1 oz",
    status: "Summer breeder · arrives mid-May",
    image: "https://images.pexels.com/photos/787067/pexels-photo-787067.jpeg",
    blurb:
      "The sound of summer in El Porvenir is the metallic trill of a male broad-tail's wings. They migrate from Mexico to nest in our mountains, hovering at every red wildflower they can find.",
    facts: [
      "Wings beat about 50 times per second.",
      "The male's wing feathers produce a metallic cricket-like trill in flight.",
      "Survives cold nights by entering torpor — heart rate drops to 50 bpm.",
      "Migrates over 2,000 miles each year between Mexico and the Rockies.",
    ],
    layout: "square",
  },
  {
    id: "sasquatch",
    number: "09",
    name: "Sasquatch",
    scientific: "Cryptid · subject of folklore",
    category: "Unverified",
    habitat: "Reported in dense conifer canyons above 7,290 ft",
    diet: "No verified data · oral accounts mention plants, berries, small game",
    size: "Reported 7–9 ft tall · 400–800 lb in accounts",
    status: "Unconfirmed by science · culturally significant across North America",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e",
    blurb:
      "Sasquatch (also called Bigfoot) is a large, ape-like figure reported across North America for over a century. It has not been confirmed by science — no verified specimen, skeleton, or DNA exists. It is included in this guide because reports cluster in the kind of old-growth conifer country found above El Porvenir, and because the figure appears in indigenous oral traditions of the region long before the modern \"Bigfoot\" era.",
    facts: [
      "No verified physical specimen — skeleton, body, or DNA sample — has ever been documented.",
      "Indigenous Salish-speaking peoples of the Pacific Northwest used the word sásq'ets, anglicized to 'Sasquatch' in the 1920s.",
      "The most-studied piece of evidence is the 1967 Patterson-Gimlin film, recorded in Bluff Creek, California; experts remain divided on its authenticity.",
      "Most reports come from forested regions above 5,000 ft with low human density — habitat that also supports large bears, which biologists consider the most common misidentification.",
    ],
    layout: "wide",
    mystery: true,
  },
];

export const HERO_IMAGE = "https://images.pexels.com/photos/32070315/pexels-photo-32070315.jpeg";
export const PAPER_TEXTURE = "https://images.pexels.com/photos/7952505/pexels-photo-7952505.jpeg";

export const findAnimal = (id) => ANIMALS.find((a) => a.id === id);
